import type { ProcessorId } from '../domain/tools/types';

export const PROCESSING_PROTOCOL_VERSION = 1 as const;

export type SerializablePrimitive = string | number | boolean | null;
export type SerializableValue =
  | SerializablePrimitive
  | readonly SerializableValue[]
  | { readonly [key: string]: SerializableValue };
export type SerializableRecord = Readonly<Record<string, SerializableValue>>;

export type ProcessingBinarySource =
  | {
      readonly kind: 'buffer';
      /** Ownership is transferred to the processing worker when a job starts. */
      readonly buffer: ArrayBuffer;
    }
  | {
      readonly kind: 'opfs';
      /** Relative path inside the application's private task directory. */
      readonly path: string;
    };

export interface ProcessingInput {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
  readonly byteLength: number;
  readonly source: ProcessingBinarySource;
}

export interface ProcessingRequest {
  readonly protocolVersion: typeof PROCESSING_PROTOCOL_VERSION;
  readonly jobId: string;
  readonly processorId: ProcessorId;
  readonly inputs: readonly ProcessingInput[];
  /** Serializable tool configuration. Sensitive values must never be persisted or logged. */
  readonly config: SerializableRecord;
}

export interface ProcessingArtifact {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
  readonly byteLength: number;
  readonly source: ProcessingBinarySource;
  /** Allows an independent validator to verify an expected PDF page count. */
  readonly expectedPageCount?: number;
}

export interface ProcessingWarning {
  /** Localization key, not rendered prose. */
  readonly code: string;
  readonly details?: SerializableRecord;
}

/** The untrusted output returned by a tool processor before validation hooks run. */
export interface ProcessorOutput {
  readonly artifacts: readonly ProcessingArtifact[];
  readonly warnings?: readonly ProcessingWarning[];
  readonly metrics?: SerializableRecord;
}

export interface OutputValidationEvidence {
  readonly artifactId: string;
  readonly validatorId: string;
  readonly facts?: SerializableRecord;
}

/** A processor output promoted to success only after all required validators pass. */
export interface ProcessingResult extends ProcessorOutput {
  readonly validation: readonly OutputValidationEvidence[];
}

function collectUniqueBuffers(
  sources: readonly ProcessingBinarySource[],
): readonly ArrayBuffer[] {
  const buffers = new Set<ArrayBuffer>();
  for (const source of sources) {
    if (source.kind === 'buffer') buffers.add(source.buffer);
  }
  return [...buffers];
}

export function collectRequestTransferables(
  request: ProcessingRequest,
): readonly ArrayBuffer[] {
  return collectUniqueBuffers(request.inputs.map(({ source }) => source));
}

export function collectResultTransferables(
  result: ProcessingResult,
): readonly ArrayBuffer[] {
  return collectUniqueBuffers(result.artifacts.map(({ source }) => source));
}

